import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useIsRTL } from '../../../hooks/useIsRTL';
import getScrollParent from 'dom-helpers/scrollParent';

import { enqueueTask } from '../../../lib/enqueueTask';
import { clamp } from '../../../lib/math';

import { useEffectEvent } from '../../../hooks/useEffectEvent';
import { useCurrentProps } from '../../../hooks/useCurrentProps';

import { advancedFlowViewStore } from './store/AdvancedFlowViewStore';

import { NodeArgumentsContext } from './card/NodeArgumentsContext';

import { AdvancedFlowCard } from './card/AdvancedFlowCard';
import { DelayCard } from './card/DelayCard';
import { StartCard } from './card/StartCard';
import { AllCard } from './card/AllCard';
import { AnyCard } from './card/AnyCard';
import { NoteCard } from './card/NoteCard';

export function useAdvancedFlowCursorCard(props, ref) {
  const isRTL = useIsRTL();
  const flowCardRef = useRef();

  const setCardPosition = useEffectEvent((clientX, clientY) => {
    const scrollParent = getScrollParent(ref.current, true);
    const scrollParentRect = scrollParent.getBoundingClientRect();
    const contentRect = ref.current.getBoundingClientRect();
    const scrollParentPositionX = scrollParent.scrollLeft;
    const clientStart = isRTL ? scrollParentRect.width - clientX : clientX;

    // Expand horizontally near the inline-end edge
    if (isRTL ? clientX < contentRect.left + 400 : clientX > contentRect.right - 400) {
      props.expandWidth(400);
    }

    // Expand vertically near the bottom edge (same in both directions)
    if (clientY > contentRect.bottom - 400) {
      props.expandHeight(400);
    }

    const style = getComputedStyle(ref.current);

    const paddingInlineStart = parseFloat(style.paddingInlineStart);
    const paddingInlineEnd = parseFloat(style.paddingInlineEnd);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);

    const top = contentRect.top;

    const { width: flowCardWidth } = flowCardRef.current?.getBoundingClientRect();

    // Prevent to drag the card outside of current visible area.
    const flowCardHalfWidth = flowCardWidth / 2;
    const clampedClientX = clamp(
      clientStart,
      scrollParentRect.left + paddingInlineStart + flowCardHalfWidth,
      scrollParentRect.right - paddingInlineEnd
    );
    const clampedClientY = clamp(
      clientY,
      scrollParentRect.top + paddingTop + 20,
      scrollParentRect.bottom - paddingBottom
    );

    // Inline-start edge in viewport coordinates
    const inlineStart = isRTL ? contentRect.right : contentRect.left;

    // Distance from inline-start, minus half the preview width
    const x = isRTL
      ? 0 - clampedClientX + flowCardHalfWidth + scrollParentPositionX
      : clampedClientX - inlineStart - flowCardHalfWidth;
    const y = clampedClientY - top - 20;

    const element = flowCardRef.current;

    if (element == null) return;

    element.style.transform = `translate(${x}px, ${y}px)`;
  });

  const currentProps = useCurrentProps({
    isPlacementDisabled: props.isPlacementDisabled,
    getPointerPosition: props.getPointerPosition,
  });

  useEffect(() => {
    if (props.cardCursorData != null) {
      const pointerPosition = currentProps.getPointerPosition();
      const { clientX, clientY } = pointerPosition ?? { clientX: 0, clientY: 0 };
      setCardPosition(clientX, clientY);

      // blur 'add' button
      enqueueTask(() => {
        document.activeElement.blur();
      });

      function onKeyDown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();

          advancedFlowViewStore.set({
            cardCursorData: null,
          });
        }
      }

      /**
       * When pressed outside cancel the cursor card. We need to include the scroll container
       * because otherwise a scroll bar press would also cause cancellation.
       */
      function pointerDownCapture(event) {
        if (currentProps.isPlacementDisabled === true) {
          return;
        }

        const target = ref.current;
        const scrollParent = getScrollParent(target, true);

        if (scrollParent.contains(event.target) === false) {
          advancedFlowViewStore.set({
            cardCursorData: null,
          });
        }
      }

      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('pointerdown', pointerDownCapture, { capture: true });

      return function () {
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('pointerdown', pointerDownCapture, { capture: true });
      };
    }
  }, [props.cardCursorData, ref, currentProps, setCardPosition]);

  function handlePointerMove(event) {
    event.preventDefault();
    event.stopPropagation();

    setCardPosition(event.clientX, event.clientY);
  }

  function handlePointerLeave(event) {
    event.preventDefault();
    event.stopPropagation();

    setCardPosition(event.clientX, event.clientY);
  }

  function handlePointerDown(event) {
    // If not left button.
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const style = getComputedStyle(ref.current);

    const paddingTop = parseFloat(style.paddingTop);

    const contentRect = ref.current.getBoundingClientRect();

    const top = contentRect.top + paddingTop;
    const left = isRTL ? contentRect.right : contentRect.left;

    const { width: flowCardWidth } = flowCardRef.current.getBoundingClientRect();
    const flowCardHalfWidth = flowCardWidth / 2;

    // Use inline-start based coordinates just like in the preview
    const x = (isRTL ? left - event.clientX : event.clientX - left) - flowCardHalfWidth;
    const y = event.clientY - top - 20;

    const snappedX = x - (x % 20);
    const snappedY = y - (y % 20);

    let removeCardCursorData = true;

    if (event.ctrlKey || event.metaKey) {
      removeCardCursorData = false;
    }

    advancedFlowViewStore.addCard({
      cardData: {
        ...props.cardCursorData.cardData,
        x: snappedX < 0 ? 0 : snappedX,
        y: snappedY < 0 ? 0 : snappedY,
      },
      removeCardCursorData: removeCardCursorData,
      shouldSelect: false,
    });
  }

  function handlePointerUp(event) {
    // If right button.
    if (event.button === 2) {
      event.preventDefault();
      event.stopPropagation();

      // Next tick because else the context menu appears.
      enqueueTask(() => {
        advancedFlowViewStore.set({
          cardCursorData: null,
        });
      });
    }
  }

  // TODO
  // Should work towards a more static version of the cards.
  function getContent() {
    switch (props.cardCursorData.cardData.type) {
      case 'delay':
        return (
          <DelayCard
            data={props.cardCursorData.cardData}
            activeConnectors={new Set()}
            nodeParents={{}}
            nodeChildren={{}}
            isInteractionDisabled={true}
          />
        );
      case 'start':
        return (
          <StartCard
            data={props.cardCursorData.cardData}
            activeConnectors={new Set()}
            nodeParents={{}}
            nodeChildren={{}}
            isInteractionDisabled={true}
          />
        );
      case 'all':
        return (
          <AllCard
            data={props.cardCursorData.cardData}
            activeConnectors={new Set()}
            nodeParents={{}}
            nodeChildren={{}}
            isInteractionDisabled={true}
          />
        );
      case 'any':
        return (
          <AnyCard
            data={props.cardCursorData.cardData}
            activeConnectors={new Set()}
            nodeParents={{}}
            nodeChildren={{}}
            isInteractionDisabled={true}
          />
        );
      case 'note':
        return (
          <NoteCard
            data={props.cardCursorData.cardData}
            activeConnectors={new Set()}
            nodeParents={{}}
            nodeChildren={{}}
            isInteractionDisabled={true}
          />
        );
      default:
        return (
          <NodeArgumentsContext>
            <AdvancedFlowCard
              data={props.cardCursorData.cardData}
              activeConnectors={new Set()}
              nodeParents={{}}
              nodeChildren={{}}
              isInteractionDisabled={true}
            />
          </NodeArgumentsContext>
        );
    }
  }

  return {
    containerProps:
      props.cardCursorData != null
        ? {
            onPointerMove(event) {
              handlePointerMove(event);
            },
            onPointerLeave(event) {
              handlePointerLeave(event);
            },
            onPointerDown(event) {
              handlePointerDown(event);
            },
            onPointerUp(event) {
              handlePointerUp(event);
            },
          }
        : {},
    component: props.cardCursorData != null && (
      <useAdvancedFlowCursorCard.Root ref={flowCardRef}>
        {getContent()}
      </useAdvancedFlowCursorCard.Root>
    ),
  };
}

useAdvancedFlowCursorCard.Root = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 0;

  pointer-events: none;
`;
